import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import {
  getRawMaterials,
  getProductProcessing,
  createProductProcessing,
  updateProductProcessing,
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";

export const IngredientsPage = () => {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showLoader, hideLoader, UserItem } = useContext(WarehouseContext);
  
  const productName = location.state?.productName || "Product";
  
  // States
  const [rawMaterialList, setRawMaterialList] = useState([]);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [existingData, setExistingData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchRawMaterials();
    fetchExistingIngredients();
  }, [productId]);

  const fetchRawMaterials = async () => {
    try {
      const response = await getRawMaterials();
      if (response.data && response.data.data) {
        setRawMaterialList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
      toast.error("Failed to fetch raw materials");
    }
  };

  const fetchExistingIngredients = async () => {
    showLoader();
    try {
      const response = await getProductProcessing({ product_id: productId });
      console.log("Existing ingredients response:", response.data);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        const productData = response.data.data.find(item => 
          String(item.product_id) === String(productId)
        );
        
        if (productData) {
          setExistingData(productData);
          
          // Parse ingredients
          let ingredients = [];
          try {
            if (productData.ingrident && Array.isArray(productData.ingrident)) {
              ingredients = productData.ingrident;
            } else if (productData.ingridents) {
              ingredients = JSON.parse(productData.ingridents);
            }
          } catch (error) {
            console.error("Error parsing ingredients:", error);
            ingredients = [];
          }
          
          setIngredientsList(ingredients);
        } else {
          setExistingData(null);
          setIngredientsList([]);
        }
      } else {
        setExistingData(null);
        setIngredientsList([]);
      }
    } catch (error) {
      console.error("Error fetching existing ingredients:", error);
      setExistingData(null);
      setIngredientsList([]);
    } finally {
      hideLoader();
    }
  };

  const handleIngredientChange = (index, field, value) => {
    const updatedIngredients = [...ingredientsList];
    updatedIngredients[index][field] = value;
    setIngredientsList(updatedIngredients);
    setHasChanges(true);
  };

  const addIngredient = () => {
 
      
    setIngredientsList([
      ...ingredientsList, 
      { id: "", qty: 0, unit: "" }
    ]);
    setHasChanges(true);
  };

  const removeIngredient = (index) => {
    const updatedIngredients = [...ingredientsList];
    updatedIngredients.splice(index, 1);
    setIngredientsList(updatedIngredients);
    setHasChanges(true);
  };

  const validateIngredients = () => {
    if (ingredientsList.length === 0) {
      toast.error("At least one ingredient is required");
      return false;
    }
    
    for (let i = 0; i < ingredientsList.length; i++) {
      const ing = ingredientsList[i];
      if (!ing.id) {
        toast.error(`Please select raw material for ingredient #${i + 1}`);
        return false;
      }
    }

    const uniqueIngredientIds = new Set();
    for (let i = 0; i < ingredientsList.length; i++) {
      if (uniqueIngredientIds.has(ingredientsList[i].id)) {
        toast.error("Duplicate ingredients are not allowed");
        return false;
      }
      uniqueIngredientIds.add(ingredientsList[i].id);
    }

    return true;
  };

  const saveIngredients = async () => {
    if (!validateIngredients()) {
      return;
    }

    showLoader();
    try {
      const payload = {
        product_id: productId,
        ingridents: JSON.stringify(ingredientsList),
        created_by: UserItem?.user_id || "USER001",
      };

      console.log("Saving ingredients payload:", payload);

      let response;
      if (existingData) {
        response = await updateProductProcessing(existingData.product_processing_id, payload);
        toast.success("Ingredients updated successfully");
      } else {
        response = await createProductProcessing(payload);
        toast.success("Ingredients added successfully");
      }

      console.log("Save response:", response);
      setIsEditing(false);
      setHasChanges(false);
      fetchExistingIngredients(); // Refresh data
    } catch (error) {
      console.error("Error saving ingredients:", error);
      toast.error(error?.response?.data?.message || "Error saving ingredients");
    } finally {
      hideLoader();
    }
  };


  const cancelEditing = () => {
    fetchExistingIngredients();
    setIsEditing(false);
    setHasChanges(false);
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const getRawMaterialName = (id) => {
    const material = rawMaterialList.find(rm => String(rm.id) === String(id));
    return material ? material.title : 'Unknown Material';
  };

   const getAvailableRawMaterials = (currentIndex) => {
    const selectedIds = ingredientsList
      .map((ing, idx) => idx !== currentIndex ? ing.id : null)
      .filter(id => id);

    return rawMaterialList.filter(rm => !selectedIds.includes(String(rm.id)));
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">Product Ingredients</h4>
          <p className="text-muted mb-0">
            <i className="bi bi-box-seam me-2"></i>
            {productName} (ID: {productId})
          </p>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          <i className="bi bi-arrow-left me-1"></i>
          Back
        </button>
      </div>

      <div className="row">
        <div className="col-md-12">
           <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="bi bi-list-ul me-2"></i>
                  Manage Ingredients
                </h6>
                <div>
                  {!isEditing && (
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={startEditing}
                    >
                      <i className="bi bi-pencil me-1"></i>
                      {ingredientsList.length > 0 ? 'Edit Ingredients' : 'Add Ingredients'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body">
              {isEditing ? (
                <>
                   <div className="d-flex justify-content-between align-items-center mb-3">
                    <label>Ingredients <span className="text-danger">*</span></label>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={addIngredient}
                    >
                      <i className="bi bi-plus"></i> Add Ingredient
                    </button>
                  </div>

                  {ingredientsList.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Raw Material <span className="text-danger">*</span></th>
                            {/* <th width="120">Quantity</th>
                            <th width="120">Unit</th> */}
                            <th width="80">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ingredientsList.map((ing, index) => (
                            <tr key={index}>
                              <td>
                                <select
                                  value={ing.id}
                                  onChange={(e) => {
                                    const raw = rawMaterialList.find(
                                      (rm) => String(rm.id) === String(e.target.value)
                                    );
                                    if (raw) {
                                      handleIngredientChange(index, "id", raw.id);
                                      // Optionally set defaults
                                    //   if (raw.default_qty && !ing.qty) {
                                    //     handleIngredientChange(index, "qty", raw.default_qty);
                                    //   }
                                    }
                                  }}
                                  className="form-control form-control-sm"
                                  required
                                >
                                  <option value="">Select Raw Material</option>
                                  {getAvailableRawMaterials(index).map((rm) => (
                                    <option key={rm.id} value={rm.id}>
                                      {rm.title}
                                    </option>
                                  ))}
                                  {/* Show currently selected item even if it would be filtered out */}
                                  {ing.id && !getAvailableRawMaterials(index).find(rm => String(rm.id) === String(ing.id)) && (
                                    <option key={ing.id} value={ing.id}>
                                      {getRawMaterialName(ing.id)}
                                    </option>
                                  )}
                                </select>
                              </td>
                              {/* <td>
                                <input
                                  type="number"
                                  placeholder="Qty"
                                  value={ing.qty || ""}
                                  onChange={(e) =>
                                    handleIngredientChange(index, "qty", e.target.value)
                                  }
                                  className="form-control form-control-sm"
                                  min="0"
                                  step="0.01"
                                />
                              </td> */}
                              {/* <td>
                                <select
                                  value={ing.unit || ""}
                                  onChange={(e) => handleIngredientChange(index, "unit", e.target.value)}
                                  className="form-control form-control-sm"
                                >
                                  <option value="">Select Unit</option>
                                  <option value="grams">Grams</option>
                                  <option value="kg">Kilograms</option>
                                  <option value="liters">Liters</option>
                                  <option value="ml">Milliliters</option>
                                  <option value="pieces">Pieces</option>
                                </select>
                              </td> */}
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeIngredient(index)}
                                  title="Remove ingredient"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      No ingredients added yet. Click "Add Ingredient" to start.
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button 
                      className="btn btn-secondary"
                      onClick={cancelEditing}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-success"
                      onClick={saveIngredients}
                      disabled={!hasChanges || ingredientsList.length === 0}
                    >
                      <i className="bi bi-check-circle me-1"></i>
                      Save Ingredients
                    </button>
                  </div>
                </>
              ) : (
                // Display Mode
                <>
                  {ingredientsList.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Raw Material</th>
                            {/* <th>Quantity</th> */}
                            {/* <th>Unit</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {ingredientsList.map((ingredient, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-box me-2 text-primary"></i>
                                  {getRawMaterialName(ingredient.id)}
                                </div>
                              </td>
                              {/* <td>
                                <span className="badge bg-light text-dark">
                                  {ingredient.qty || 'N/A'}
                                </span>
                              </td> */}
                              {/* <td>
                                <span className="badge bg-secondary">
                                  {ingredient.unit || 'N/A'}
                                </span>
                              </td> */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="bi bi-inbox display-4 text-muted"></i>
                      <h5 className="text-muted mt-3">No Ingredients Found</h5>
                      <p className="text-muted">This product doesn't have any ingredients configured yet.</p>
                      <button 
                        className="btn btn-primary"
                        onClick={startEditing}
                      >
                        <i className="bi bi-plus me-1"></i>
                        Add First Ingredient
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-4 d-none">
          {/* Summary Card */}
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Summary
              </h6>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-2">
                <span>Total Ingredients:</span>
                <strong className="text-primary">{ingredientsList.length}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Product ID:</span>
                <strong>{productId}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Product Name:</span>
                <strong>{productName}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Status:</span>
                <strong className={existingData ? "text-success" : "text-warning"}>
                  {existingData ? "Configured" : "Not Configured"}
                </strong>
              </div>
              {hasChanges && (
                <div className="alert alert-warning alert-sm mt-3">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  You have unsaved changes
                </div>
              )}
            </div>
          </div>

          {/* Available Raw Materials Card */}
          <div className="card mt-3">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="bi bi-collection me-2"></i>
                Available Raw Materials
              </h6>
            </div>
            <div className="card-body">
              <div className="max-height-200 overflow-auto">
                {rawMaterialList.length > 0 ? (
                  rawMaterialList.map((material, index) => (
                    <div key={material.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                      <small>{material.title}</small>
                      <span className="badge bg-light text-dark">{material.id}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No raw materials available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .max-height-200 {
          max-height: 200px;
        }
        .alert-sm {
          padding: 0.5rem;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
};
